import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Calendar, Clock, User, Tag, Share2, 
  Facebook, Twitter, Linkedin, Copy, Home, Building,
  ArrowRight, CheckCircle, Loader2
} from 'lucide-react';
import { staticBlogPosts } from './Blog';
import { useToast } from '../hooks/use-toast';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState(staticBlogPosts);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        // Try to fetch from API first
        const response = await fetch(`${API_URL}/api/blog/posts/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setPost({
            ...data,
            id: data.slug || data.id,
            date: data.published_at || data.created_at || new Date().toISOString(),
            readTime: data.read_time || '5 min read'
          });
        } else {
          // Fall back to static posts
          const staticPost = staticBlogPosts.find(p => p.id === slug);
          setPost(staticPost);
        }
        
        // Fetch all posts for related posts
        const allResponse = await fetch(`${API_URL}/api/blog/posts?published_only=true`);
        if (allResponse.ok) {
          const posts = await allResponse.json();
          const formattedPosts = posts.map(p => ({
            ...p,
            id: p.slug || p.id,
            date: p.published_at || p.created_at
          }));
          setAllPosts([...formattedPosts, ...staticBlogPosts]);
        }
      } catch (error) {
        console.error('Error fetching post:', error);
        // Fall back to static posts
        const staticPost = staticBlogPosts.find(p => p.id === slug);
        setPost(staticPost);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
    window.scrollTo(0, 0);
  }, [slug]);

  // Related posts (same category, different post)
  const relatedPosts = post 
    ? allPosts.filter(p => p.category === post.category && (p.id !== post.id && p.slug !== post.slug)).slice(0, 3)
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-32 pb-12 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF8C42]" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 pt-32 pb-12">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold text-[#404040] mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-6">The article you're looking for doesn't exist.</p>
          <Link to="/blog">
            <Button className="bg-[#FF8C42] hover:bg-[#FF6B35]">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = window.location.href;
  const shareText = `${post.title} - Harry & Jacobs`;

  const handleShare = (platform) => {
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    };
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast({ title: "Link Copied!", description: "Article link copied to clipboard" });
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Article Header */}
      <article>
        <header className="bg-gradient-to-br from-[#404040] to-[#333] text-white py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/blog" className="inline-flex items-center gap-2 text-gray-300 hover:text-white mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
            
            <div className="flex items-center gap-2 mb-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                post.category === 'landlord' 
                  ? 'bg-[#FF8C42] text-white' 
                  : 'bg-blue-500 text-white'
              }`}>
                {post.category === 'landlord' ? 'Landlord Guide' : 'Architecture'}
              </span>
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              {post.title}
            </h1>
            
            <p className="text-xl text-gray-300 mb-6">{post.excerpt}</p>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-300">
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {post.author}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(post.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        <div className="max-w-5xl mx-auto px-4 -mt-8 mb-8">
          <img 
            src={post.image} 
            alt={post.title}
            className="w-full h-64 md:h-96 object-cover rounded-xl shadow-xl"
          />
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-8">
              <Card className="shadow-lg">
                <CardContent className="p-6 md:p-10">
                  <div className="prose prose-lg max-w-none prose-headings:text-[#404040] prose-a:text-[#FF8C42] prose-strong:text-[#404040]">
                    <ReactMarkdown
                      components={{
                        h2: ({children}) => <h2 className="text-2xl font-bold text-[#404040] mt-8 mb-4 pb-2 border-b">{children}</h2>,
                        h3: ({children}) => <h3 className="text-xl font-bold text-[#404040] mt-6 mb-3">{children}</h3>,
                        h4: ({children}) => <h4 className="text-lg font-bold text-[#404040] mt-4 mb-2">{children}</h4>,
                        p: ({children}) => <p className="text-gray-700 leading-relaxed mb-4">{children}</p>,
                        ul: ({children}) => <ul className="list-disc pl-6 mb-4 space-y-2">{children}</ul>,
                        ol: ({children}) => <ol className="list-decimal pl-6 mb-4 space-y-2">{children}</ol>,
                        li: ({children}) => <li className="text-gray-700">{children}</li>,
                        strong: ({children}) => <strong className="font-bold text-[#404040]">{children}</strong>,
                        a: ({href, children}) => (
                          <Link to={href} className="text-[#FF8C42] hover:underline font-medium">
                            {children}
                          </Link>
                        ),
                        table: ({children}) => (
                          <div className="overflow-x-auto my-6">
                            <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                              {children}
                            </table>
                          </div>
                        ),
                        thead: ({children}) => <thead className="bg-gray-100">{children}</thead>,
                        th: ({children}) => <th className="px-4 py-3 text-left text-sm font-semibold text-[#404040] border-b">{children}</th>,
                        td: ({children}) => <td className="px-4 py-3 text-sm text-gray-700 border-b">{children}</td>,
                        blockquote: ({children}) => (
                          <blockquote className="border-l-4 border-[#FF8C42] pl-4 my-4 italic text-gray-600">
                            {children}
                          </blockquote>
                        ),
                      }}
                    >
                      {post.content}
                    </ReactMarkdown>
                  </div>

                  {/* Tags */}
                  <div className="mt-8 pt-6 border-t">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Tag className="w-4 h-4 text-gray-400" />
                      {post.tags.map(tag => (
                        <Link 
                          to={`/blog?search=${encodeURIComponent(tag)}`}
                          key={tag}
                          className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm hover:bg-[#FF8C42] hover:text-white transition-colors"
                        >
                          {tag}
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Share */}
                  <div className="mt-6 pt-6 border-t">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-500 flex items-center gap-2">
                        <Share2 className="w-4 h-4" />
                        Share:
                      </span>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleShare('facebook')}
                          className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
                        >
                          <Facebook className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleShare('twitter')}
                          className="p-2 bg-sky-500 text-white rounded-full hover:bg-sky-600 transition-colors"
                        >
                          <Twitter className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleShare('linkedin')}
                          className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors"
                        >
                          <Linkedin className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={copyLink}
                          className="p-2 bg-gray-200 text-gray-600 rounded-full hover:bg-gray-300 transition-colors"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4 space-y-6">
              {/* CTA */}
              <Card className={`${post.category === 'landlord' ? 'bg-gradient-to-br from-[#FF8C42] to-[#FF6B35]' : 'bg-gradient-to-br from-blue-500 to-blue-600'} text-white`}>
                <CardContent className="p-6">
                  {post.category === 'landlord' ? (
                    <>
                      <Home className="w-10 h-10 mb-4" />
                      <h3 className="font-bold text-xl mb-2">Free Rental Valuation</h3>
                      <p className="text-sm opacity-90 mb-4">
                        Find out how much your property could earn.
                      </p>
                      <Link to="/landlord/free-valuation">
                        <Button className="w-full bg-white text-[#FF8C42] hover:bg-gray-100">
                          Get Free Valuation
                        </Button>
                      </Link>
                    </>
                  ) : (
                    <>
                      <Building className="w-10 h-10 mb-4" />
                      <h3 className="font-bold text-xl mb-2">Free Design Quote</h3>
                      <p className="text-sm opacity-90 mb-4">
                        Get expert architectural advice today.
                      </p>
                      <Link to="/architecture/quote">
                        <Button className="w-full bg-white text-blue-600 hover:bg-gray-100">
                          Get Free Quote
                        </Button>
                      </Link>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Author */}
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center gap-4 mb-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_harry-and-jacobs/artifacts/7nr8z83x_Insta%20Profile%20Pic.jpg"
                      alt="Harry & Jacobs"
                      className="w-14 h-14 rounded-full"
                    />
                    <div>
                      <h4 className="font-bold text-[#404040]">{post.author}</h4>
                      <p className="text-sm text-gray-500">Property Experts</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    15+ years of experience in Harrow property market. Helping landlords and homeowners achieve their property goals.
                  </p>
                </CardContent>
              </Card>

              {/* Quick Contact */}
              <Card className="bg-gray-50">
                <CardContent className="p-5">
                  <h4 className="font-bold text-[#404040] mb-3">Need Help?</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Our team is ready to answer your questions.
                  </p>
                  <a href="tel:02081898889" className="flex items-center gap-2 text-[#FF8C42] font-medium">
                    📞 020 8189 8889
                  </a>
                </CardContent>
              </Card>
            </aside>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-[#404040] mb-6">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map(relatedPost => (
                <Link to={`/blog/${relatedPost.id}`} key={relatedPost.id}>
                  <Card className="h-full hover:shadow-lg transition-shadow overflow-hidden group">
                    <div className="h-40 overflow-hidden">
                      <img 
                        src={relatedPost.image} 
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-bold text-[#404040] mb-2 group-hover:text-[#FF8C42] transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {relatedPost.readTime}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-12 bg-[#404040]">
        <div className="max-w-3xl mx-auto px-4 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-gray-300 mb-6">
            {post.category === 'landlord' 
              ? 'Let us help you maximize your rental income.'
              : 'Transform your property with expert architectural design.'}
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to={post.category === 'landlord' ? '/landlord/free-valuation' : '/architecture/quote'}>
              <Button size="lg" className="bg-[#FF8C42] hover:bg-[#FF6B35]">
                {post.category === 'landlord' ? 'Get Free Valuation' : 'Get Free Quote'}
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#404040]">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
